import { useMemo, useState } from "react";
import { Card, DatePicker, Form, InputNumber, Select, Typography } from "antd";
import { Dayjs } from "dayjs";

const DEFAULT_ENTITLEMENT_DAYS = 28;
const DEFAULT_DAYS_IN_YEAR = 365;

// Pro-rata entitlement for a period, same as the HR Excel sheet:
// entitlement / daysInYear * (endDate - startDate).
// The day difference is exclusive of the end date (Excel B3-B2), so
// 01.10.2026 -> 01.01.2027 = 92 days -> 28 / 365 * 92 = 7.06.
const LeaveCalculator = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [entitlementDays, setEntitlementDays] = useState<number | null>(
    DEFAULT_ENTITLEMENT_DAYS,
  );
  const [daysInYear, setDaysInYear] = useState<number>(DEFAULT_DAYS_IN_YEAR);

  const periodDays = useMemo(() => {
    if (!startDate || !endDate) return null;
    return endDate.startOf("day").diff(startDate.startOf("day"), "day");
  }, [startDate, endDate]);

  const result = useMemo(() => {
    if (periodDays === null || periodDays < 0 || !entitlementDays) return null;
    return (entitlementDays / daysInYear) * periodDays;
  }, [periodDays, entitlementDays, daysInYear]);

  const invalidRange = periodDays !== null && periodDays < 0;

  return (
    <div className="leave-calculator">
      <Typography.Title level={2}>Leave Calculator</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 640 }}>
        Calculate how many annual leave days you are entitled to for a given
        period. Select the start and end dates, adjust the yearly entitlement
        (28 by default) and the number of days in the year (365 or 366) if
        needed. The result is calculated as: annual leave days / days in year
        × number of days in the period.
      </Typography.Paragraph>
      <Card style={{ maxWidth: 480 }}>
        <Form layout="vertical">
          <Form.Item label="Start date">
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              format="DD.MM.YYYY"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="End date"
            validateStatus={invalidRange ? "error" : undefined}
            help={invalidRange ? "End date must be after start date" : undefined}
          >
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              format="DD.MM.YYYY"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Annual leave days (per year)">
            <InputNumber
              min={0}
              max={366}
              value={entitlementDays}
              onChange={setEntitlementDays}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Days in year">
            <Select
              value={daysInYear}
              onChange={setDaysInYear}
              options={[
                { value: 365, label: "365" },
                { value: 366, label: "366" },
              ]}
            />
          </Form.Item>
        </Form>

        <Typography.Text type="secondary">
          Days in period: {periodDays !== null && !invalidRange ? periodDays : "-"}
        </Typography.Text>
        <Typography.Title level={3} style={{ marginTop: 8 }}>
          Leave days: {result !== null ? result.toFixed(2) : "-"}
        </Typography.Title>
      </Card>
    </div>
  );
};

export default LeaveCalculator;
